import React from 'react'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Security = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Guild is designed with security in mind. Authentication is passwordless,
        which means there are no passwords to leak, forget, or reuse across
        services. Every sign-in is verified through your email or a connected
        Google account.
      </Paragraph>
      <DocScreenshot
        src='docs/account/sign-in-page.png'
        alt='Sign in page with email input and Continue with Google option — passwordless authentication means no passwords to manage'
        caption='Guild uses passwordless authentication — no passwords to leak or forget'
      />
    </Card>

    <Card gap='$4'>
      <DocSection id='passwordless-auth' title='Passwordless Authentication'>
        <Paragraph size='$5'>
          When you sign in to Guild, you receive a one-time 5-digit verification
          code via email. This code expires after 10 minutes and can only be used
          once. Because there are no stored passwords, common attack vectors like
          credential stuffing and password database breaches do not apply.
        </Paragraph>
        <Paragraph size='$5'>
          For the full sign-in walkthrough, see{' '}
          <Link href='/docs/account/sign-in'>Sign Up & Sign In</Link>.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='sessions' title='Session Management'>
        <Paragraph size='$5'>
          After signing in, Guild creates a session that keeps you authenticated
          as you browse the platform. Sessions are maintained securely using
          HTTP-only cookies that are not accessible to JavaScript, protecting
          against cross-site scripting attacks.
        </Paragraph>
        <Paragraph size='$5'>
          You can sign out at any time by clicking your profile photo in the
          top-right corner and selecting{' '}
          <Paragraph size='$5' fontWeight='$7'>Sign Out</Paragraph>. This ends
          your current session immediately.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='connected-accounts' title='Connected Accounts'>
        <Paragraph size='$5'>
          You can connect your Google account to Guild for one-click sign-in.
          When connected, clicking{' '}
          <Paragraph size='$5' fontWeight='$7'>Continue with Google</Paragraph>{' '}
          on the sign-in page authenticates you instantly without needing an email
          code.
        </Paragraph>
        <Paragraph size='$5'>
          Google account connections are also used for{' '}
          <Link href='/docs/account/calendar'>Calendar Sync</Link>, which
          automatically adds Events to your Google Calendar when you RSVP. You
          can manage connected accounts from your account Settings.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='data-privacy' title='Your Data'>
        <Paragraph size='$5'>
          Guild only collects the information needed to provide the service —
          your email address, display name, and any profile details you choose to
          add. Your email address is used for authentication and notifications,
          and is not shared publicly unless you choose to display it.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Security
